import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ImageIcon, Upload, X } from 'lucide-react';
import { z } from 'zod';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Textarea } from '@/shared/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import {
  createArticulo,
  deleteArticuloImage,
  fetchArticuloImageBlobUrl,
  parseArticuloNumber,
  updateArticulo,
  uploadArticuloImage,
  cuentaParaTipoProducto,
  type ArticuloItem,
  type CotizableFlag,
} from '@/shared/api/controllers/articulos';
import { listActiveCuentasContables } from '@/shared/api/controllers/cuentasContables';
import { compressImage } from '@/shared/lib/compressImage';
import { ErrorDialog } from '@/shared/components/ui/status-dialog';
import { parseMoney } from '@/shared/lib/money';
import { MoneyInput } from '@/features/ventas/components/MoneyInput';
import { invalidateAbmRelatedQueries } from '@/shared/api/abmCacheInvalidation';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import { formatIvaRate } from '@/shared/countries';

/**
 * Browser-side upload cap. Vercel serverless functions cap request bodies
 * at 4.5 MB across all plans; we keep a safety margin for the multipart
 * envelope overhead.
 */
const MAX_UPLOAD_BYTES = 4 * 1024 * 1024;

const schema = z.object({
  articulo: z.string().trim().min(1, 'Ingresá la descripción'),
  codigo: z.string().trim().min(1, 'Ingresá el código'),
  modelo: z.string().optional(),
  descripcion: z.string().optional(),
  tipoProducto: z.string().trim().min(1, 'Seleccioná el tipo'),
  valorUn: z.coerce.number({ invalid_type_error: 'Número inválido' }).nonnegative().optional(),
  valorUnDolar: z.coerce.number({ invalid_type_error: 'Número inválido' }).nonnegative().optional(),
  costoContable: z.coerce.number({ invalid_type_error: 'Número inválido' }).nonnegative().optional(),
  iva: z.string().min(1, 'Seleccioná el IVA'),
  electrico: z.boolean().optional(),
  /** Vacío = sin duración (al venderlo con garantía se pide la fecha a mano). */
  garantiaMeses: z
    .string()
    .trim()
    .refine((v) => v === '' || (/^\d+$/.test(v) && Number(v) > 0), 'Meses enteros mayores a 0')
    .optional(),
});
type Values = z.infer<typeof schema>;

function itemToForm(item: ArticuloItem): Values {
  return {
    articulo: item.fields.Articulo_ART ?? '',
    codigo: item.fields.Codigo_ART ?? '',
    modelo: item.fields.Modelo_ART ?? '',
    descripcion: item.fields.Descripcion_ART ?? '',
    tipoProducto: item.fields.TipoProducto_ART ?? '',
    valorUn: parseArticuloNumber(item.fields.ValorUn_ART) ?? undefined,
    valorUnDolar: parseArticuloNumber(item.fields.ValorUnDolar_ART) ?? undefined,
    costoContable: parseArticuloNumber(item.fields.CostoContable_ART) ?? undefined,
    // Preserve whatever's stored — the country pack drives which
    // values the dropdown lets the user pick going forward, but legacy
    // rows from a different country still display correctly.
    iva: (item.fields.IVA_ART ?? '').trim() || '',
    electrico: item.fields.Electrico_ART === 'SI',
    garantiaMeses: item.fields.GarantiaMeses_ART ?? '',
  };
}

const CATEGORIAS_PRODUCTO = ['Hornos', 'Accesorios', 'Repuestos'] as const;

// Costo Contable applies to ALL MX articles now (no category gate). It
// feeds the Nova economics (factura mínima + Factura Interna amount) for
// any line whose article carries a costo — hornos, repuestos/refacciones
// and accesorios — so a mixed order facturado por Nova accounts for every
// item's cost. (Widened from the prior Hornos-only scope on 2026-06-08
// after the client sent costs for accesorios/refacciones.)

// RHF setValueAs para inputs de moneda. Acepta cualquier combinación
// de separadores que use el operador (es-MX `$8,343.35`, es-AR
// `$8.343,35`, o sin separador de miles). El separador decimal es
// "el último `.` o `,` que aparece"; los anteriores se tratan como
// separadores de miles y se descartan. Devuelve `undefined` para
// entradas vacías para que zod `.optional()` las trate como ausentes
// en vez de `NaN`.
const parseMoneyInput = (raw: unknown): number | undefined => {
  if (raw === '' || raw == null) return undefined;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : undefined;
  const s = String(raw).trim();
  if (!s) return undefined;
  const lastDot = s.lastIndexOf('.');
  const lastComma = s.lastIndexOf(',');
  let normalized: string;
  if (lastDot === -1 && lastComma === -1) {
    normalized = s;
  } else if (lastDot > lastComma) {
    // `.` es decimal — eliminar `,` (separador de miles).
    normalized = s.replace(/,/g, '');
  } else {
    // `,` es decimal — eliminar `.` y convertir la última `,` a `.`.
    normalized = s.replace(/\./g, '').replace(',', '.');
  }
  const n = Number(normalized);
  return Number.isFinite(n) ? n : undefined;
};

const emptyValues: Values = {
  articulo: '',
  codigo: '',
  modelo: '',
  descripcion: '',
  tipoProducto: '',
  valorUn: undefined,
  valorUnDolar: undefined,
  costoContable: undefined,
  iva: '21',
  electrico: false,
  garantiaMeses: '',
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item?: ArticuloItem | null;
  existingRows: ArticuloItem[];
  cotizable: CotizableFlag;
  queryKey: readonly string[];
  entitySingular: string;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
};

export function ArticuloDialog({
  open,
  onOpenChange,
  item,
  existingRows,
  cotizable,
  queryKey,
  entitySingular,
  onSuccess,
  onError,
}: Props) {
  const queryClient = useQueryClient();
  const isEdit = Boolean(item);
  const { config: activeCountry } = useActiveCountry();
  const isMx = activeCountry.code === 'MX';

  // Default IVA value comes from the active country pack so a new
  // article on MX starts at 16% (vs AR's 21%). Existing rows preserve
  // whatever's stored in `IVA_ART`.
  const defaults = useMemo(
    () =>
      item
        ? itemToForm(item)
        : { ...emptyValues, iva: formatIvaRate(activeCountry.ivaRateDefault) },
    [item, activeCountry.ivaRateDefault],
  );

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: defaults,
  });

  // Image state. `pendingFile` is a newly-picked file waiting to be uploaded
  // after the articulo row is created/patched. `previewUrl` is either a
  // blob URL (local preview or the existing image fetched from the server).
  // `removedExisting` means the user clicked the X on an already-attached
  // image — we issue a DELETE on save. `previewLoading` drives the
  // skeleton placeholder while the existing image is being retrieved.
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Generation counter for in-flight preview fetches. Any user action that
  // supersedes the fetch (pick, remove, dialog close/reopen) bumps this,
  // so the async resolution knows to drop its result instead of clobbering
  // the user's newer choice.
  const fetchGenRef = useRef(0);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [removedExisting, setRemovedExisting] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [sizeError, setSizeError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    form.reset(defaults);
    setPendingFile(null);
    setRemovedExisting(false);
    setPreviewFailed(false);
    setPreviewUrl(null);
    if (!item) {
      setPreviewLoading(false);
      return;
    }

    // Fetch the existing image with auth (browsers don't attach the bearer
    // token to <img src> requests, so we have to pull bytes and hand the
    // <img> a blob URL). The generation ref guards against three races:
    // dialog reopened with a different item, user picks a new file, user
    // clicks the X to remove — any of those must drop this fetch's result.
    const gen = ++fetchGenRef.current;
    setPreviewLoading(true);
    fetchArticuloImageBlobUrl(item.id)
      .then((url) => {
        if (fetchGenRef.current !== gen) {
          if (url) URL.revokeObjectURL(url);
          return;
        }
        setPreviewLoading(false);
        if (url) setPreviewUrl(url);
      })
      .catch(() => {
        if (fetchGenRef.current !== gen) return;
        setPreviewLoading(false);
        setPreviewFailed(true);
      });
    return () => {
      // Bump so a late resolution is ignored.
      fetchGenRef.current++;
    };
  }, [open, defaults, form, item]);

  // Revoke blob URLs as soon as they're replaced so we don't leak memory.
  useEffect(() => {
    return () => {
      if (previewUrl?.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleFilePick = async (file: File | null) => {
    // Any in-flight preview fetch is now stale — its result must be dropped.
    fetchGenRef.current++;
    setPreviewLoading(false);
    if (previewUrl?.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
    if (!file) {
      setPendingFile(null);
      setPreviewUrl(null);
      return;
    }

    // Compress images in-browser so phone photos (often 6–12 MB) fit
    // under Vercel's 4.5 MB function body cap. Non-images pass through
    // and fail the size check below if too big.
    setCompressing(true);
    let picked: File | null;
    try {
      picked = await compressImage(file, { maxBytes: MAX_UPLOAD_BYTES });
    } catch {
      picked = null;
    } finally {
      setCompressing(false);
    }

    if (!picked) {
      setSizeError(
        'La imagen supera el tamaño máximo permitido (4 MB, incluso tras comprimir). Usá una más liviana.',
      );
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setPendingFile(picked);
    setRemovedExisting(false);
    setPreviewFailed(false);
    setPreviewUrl(URL.createObjectURL(picked));
  };

  const handleRemoveImage = () => {
    fetchGenRef.current++;
    setPreviewLoading(false);
    if (previewUrl?.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
    setPendingFile(null);
    setPreviewUrl(null);
    setPreviewFailed(false);
    // Only flag for server-side delete if the articulo already had one.
    if (isEdit) setRemovedExisting(true);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const { data: cuentasContables = [] } = useQuery({
    queryKey: ['configuracion', 'cuentas-contables', 'active', activeCountry.code],
    queryFn: listActiveCuentasContables,
    staleTime: 60_000,
  });

  const iva = form.watch('iva');
  const tipoProducto = form.watch('tipoProducto');
  const watchedArticulo = form.watch('articulo');
  const watchedCodigo = form.watch('codigo');
  // Costo Contable applies to every MX article (any category). It feeds
  // the Nova flow for whatever carries a cost. Hidden only for AR.
  const showCostoContable = isMx;

  const missingFields = useMemo(() => {
    const missing: string[] = [];
    if (!watchedArticulo?.trim()) missing.push('Descripción');
    if (!watchedCodigo?.trim()) missing.push('Código');
    if (!tipoProducto) missing.push('Tipo');
    if (!iva) missing.push('% IVA');
    return missing;
  }, [watchedArticulo, watchedCodigo, tipoProducto, iva]);

  const canSubmit = missingFields.length === 0;

  const mutation = useMutation({
    mutationFn: async (values: Values) => {
      // The picker chooses the PRODUCT TYPE (Hornos/Accesorios/Repuestos); the
      // accounting Cuenta Contable y la categoría se derivan de él según el
      // plan de cuentas del país activo (AR fusionó en Mercaderias/Insumos,
      // MX conserva Hornos/Accesorios/Repuestos bajo 'Activos'). La categoría
      // acompaña a la cuenta para que `category_id` quede consistente y para
      // desambiguar cuentas con el mismo nombre en distintas categorías.
      const { cuenta: cuentaContable, categoria } = cuentaParaTipoProducto(
        values.tipoProducto,
        activeCountry.code,
      );
      // Autocomplete backing flags from the derived cuenta contable, mirroring
      // PowerApps: Comprable always SI for Productos; ImpactaStock + LlevaNroSerie
      // inherit from ABM_CuentaContable.ImpactaStock_CC / NroSerie_CC.
      // Match on BOTH cuenta name AND categoría: "Mercaderias" exists under two
      // active accounts (Mercaderia-Productos 899 serial+stock vs
      // Mercaderia-Reparaciones 920 neither) — a name-only .find could pick the
      // wrong one and save a Horno with lleva_nro_serie=false (serial loss at
      // despacho). `categoria` es la que devolvió cuentaParaTipoProducto.
      const cc = cuentasContables.find(
        (c) =>
          (c.fields.CuentaContable_CC ?? '') === cuentaContable &&
          (categoria == null || (c.fields.Categoria_CC ?? '') === categoria),
      );
      const impactaStock = cc?.fields.ImpactaStock_CC === 'SI';
      const llevaNroSerie = cc?.fields.NroSerie_CC === 'SI';

      const payload = {
        articulo: values.articulo,
        codigo: values.codigo,
        modelo: values.modelo,
        descripcion: values.descripcion,
        cuentaContable,
        categoria,
        productType: values.tipoProducto,
        valorUn: values.valorUn ?? null,
        valorUnDolar: values.valorUnDolar ?? null,
        // Costo Contable lives on MX articles (any category). Persist
        // whatever the form holds; AR omits the field entirely so the
        // column stays untouched.
        ...(isMx ? { costoContable: values.costoContable ?? null } : {}),
        iva: values.iva,
        cotizable,
        comprable: true,
        llevaNroSerie,
        impactaStock,
        electrico: values.electrico ?? false,
        garantiaMeses: values.garantiaMeses ? Number(values.garantiaMeses) : null,
      };
      const saved =
        isEdit && item
          ? await updateArticulo(item.id, payload)
          : await createArticulo(payload);

      // Image side-effects. Two-step (row first, image second) so a
      // transient upload failure doesn't orphan the articulo.
      if (pendingFile) {
        await uploadArticuloImage(saved.id, pendingFile);
      } else if (removedExisting && isEdit && item) {
        await deleteArticuloImage(saved.id);
      }
      return saved;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      // Cross-feature: Compras article picker, Cotización picker,
      // EditarCotizacion picker, etc. all derive from the articles
      // catalogue. Without this invalidate they keep serving the
      // pre-edit list until staleTime (5 min for Compras).
      invalidateAbmRelatedQueries(queryClient, 'articulos');
      onSuccess(
        isEdit
          ? `El ${entitySingular.toLowerCase()} se actualizó correctamente.`
          : `El ${entitySingular.toLowerCase()} se creó correctamente.`,
      );
    },
    onError: (err) =>
      onError(
        err instanceof Error
          ? err.message
          : `No se pudo guardar el ${entitySingular.toLowerCase()}.`,
      ),
  });

  const onSubmit = form.handleSubmit((values) => {
    const normalized = values.codigo.trim().toLowerCase();
    const duplicated = existingRows.find(
      (r) =>
        r.id !== item?.id &&
        (r.fields.Codigo_ART ?? '').trim().toLowerCase() === normalized,
    );
    if (duplicated) {
      form.setError('codigo', {
        type: 'manual',
        message: `Ya existe un ${entitySingular.toLowerCase()} con ese código.`,
      });
      return;
    }
    mutation.mutate(values);
  });

  const fieldError = (name: keyof Values) =>
    form.formState.errors[name]?.message as string | undefined;

  return (
    <Dialog open={open} onOpenChange={(o) => !mutation.isPending && onOpenChange(o)}>
      <DialogContent
        className="sm:max-w-3xl"
        processing={mutation.isPending && (isEdit ? 'Guardando cambios…' : 'Creando producto…')}
      >
        <DialogHeader>
          <DialogTitle>
            {isEdit ? `Editar ${entitySingular}` : `Agregar ${entitySingular}`}
          </DialogTitle>
          <DialogDescription>
            Completá los datos del {entitySingular.toLowerCase()}.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="flex flex-1 flex-col overflow-hidden" noValidate>
          <DialogBody className="space-y-4">
            <div className="grid gap-4 md:grid-cols-3">
              <Field label="Descripción" required error={fieldError('articulo')}>
                <Input
                  variant="filled"
                  placeholder="Ingresar Desc. del Articulo"
                  {...form.register('articulo')}
                />
              </Field>
              <Field label="Modelo" error={fieldError('modelo')}>
                <Input
                  variant="filled"
                  placeholder="Ingresar Modelo del Articulo"
                  {...form.register('modelo')}
                />
              </Field>
              <Field label="Código" required error={fieldError('codigo')}>
                <Input
                  variant="filled"
                  placeholder="Ingresar Código del Articulo"
                  {...form.register('codigo')}
                />
              </Field>
            </div>

            {/*
             * Money row. AR uses 4 columns (Categoría · Valor USD ·
             * Valor Unitario · % IVA). When the active country is MX
             * AND the picked Categoría is Hornos, Costo Contable
             * slots in between Categoría and the price columns,
             * pushing the row to 5 columns. Other MX categories use
             * the same 4-column layout as AR — Costo Contable is
             * irrelevant outside of oven sales (Nova flow).
             */}
            <div className={showCostoContable ? 'grid gap-4 md:grid-cols-5' : 'grid gap-4 md:grid-cols-4'}>
              <Field label="Tipo" required error={fieldError('tipoProducto')}>
                <Select
                  value={tipoProducto ?? ''}
                  onValueChange={(v) =>
                    form.setValue('tipoProducto', v, {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIAS_PRODUCTO.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              {showCostoContable && (
                <Field label="Costo Contable" error={fieldError('costoContable')}>
                  <Input
                    type="text"
                    inputMode="decimal"
                    variant="filled"
                    placeholder="Ingresar Costo Contable"
                    {...form.register('costoContable', { setValueAs: parseMoneyInput })}
                  />
                </Field>
              )}
              <Field label="Valor (USD)" error={fieldError('valorUnDolar')}>
                <Controller
                  control={form.control}
                  name="valorUnDolar"
                  render={({ field }) => (
                    <PrecioInput
                      moneda="USD"
                      placeholder="Ingresar Val. Unitario"
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </Field>
              <Field
                label={isMx ? 'Valor (MXN)' : 'Valor (ARS)'}
                error={fieldError('valorUn')}
              >
                <Controller
                  control={form.control}
                  name="valorUn"
                  render={({ field }) => (
                    <PrecioInput
                      moneda={isMx ? 'MXN' : 'ARS'}
                      placeholder="Ingresar Val. Unitario"
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </Field>
              <Field label="% IVA" required error={fieldError('iva')}>
                <Select
                  value={iva}
                  onValueChange={(v) =>
                    form.setValue('iva', v as Values['iva'], { shouldDirty: true })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {activeCountry.ivaRates.map((rate) => {
                      const v = formatIvaRate(rate);
                      return (
                        <SelectItem key={v} value={v}>
                          {v} %
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <Field label="Garantía (meses)" error={fieldError('garantiaMeses')}>
              <Input
                type="text"
                inputMode="numeric"
                variant="filled"
                placeholder="Sin garantía cargada"
                {...form.register('garantiaMeses')}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Duración de la garantía desde la entrega. Si queda vacío y se vende con garantía,
                se pide la fecha de vencimiento a mano.
              </p>
            </Field>

            <label className="flex items-center justify-between gap-3 rounded-lg border bg-secondary/30 px-3 py-2 text-sm">
              <span className="space-y-0.5">
                <span className="block font-medium text-foreground">Eléctrico</span>
                <span className="block text-xs text-muted-foreground">
                  Marcar si el producto es 100% eléctrico. Las puestas en marcha de
                  ventas con artículos eléctricos no piden los videos de combustión.
                </span>
              </span>
              <input
                type="checkbox"
                className="h-4 w-4 accent-primary"
                checked={form.watch('electrico') ?? false}
                onChange={(e) =>
                  form.setValue('electrico', e.target.checked, { shouldDirty: true })
                }
              />
            </label>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={(e) => void handleFilePick(e.target.files?.[0] ?? null)}
            />
            {previewLoading || compressing ? (
              <div className="flex items-center gap-3 rounded-md border bg-secondary/30 p-2">
                <div className="h-20 w-20 animate-pulse rounded bg-muted" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 w-2/3 animate-pulse rounded bg-muted" />
                  <div className="h-3 w-1/3 animate-pulse rounded bg-muted" />
                  {compressing && (
                    <p className="text-xs text-muted-foreground">
                      Comprimiendo imagen…
                    </p>
                  )}
                </div>
              </div>
            ) : previewUrl && !previewFailed ? (
              <div className="flex items-center gap-3 rounded-md border bg-secondary/30 p-2">
                <img
                  src={previewUrl}
                  alt="Vista previa"
                  className="h-20 w-20 rounded object-cover"
                  onError={() => setPreviewFailed(true)}
                />
                <div className="flex-1 text-sm text-muted-foreground">
                  {pendingFile
                    ? pendingFile.name
                    : 'Imagen actual del artículo.'}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Cambiar
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={handleRemoveImage}
                  aria-label="Quitar imagen"
                  title="Quitar imagen"
                >
                  <X className="h-4 w-4" aria-hidden />
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                className="w-full gap-2"
                onClick={() => fileInputRef.current?.click()}
              >
                {previewFailed ? (
                  <ImageIcon className="h-4 w-4" aria-hidden />
                ) : (
                  <Upload className="h-4 w-4" aria-hidden />
                )}
                Subir Imagen
              </Button>
            )}

            <Field label="Observación" error={fieldError('descripcion')}>
              <Textarea
                variant="filled"
                rows={5}
                placeholder="Ingresar Observación"
                {...form.register('descripcion')}
              />
            </Field>
          </DialogBody>

          <DialogFooter>
            {missingFields.length > 0 && (
              <p className="mr-auto text-xs text-muted-foreground">
                Faltan completar: <span className="font-medium text-destructive">{missingFields.join(', ')}</span>
              </p>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending || !canSubmit}>
              {isEdit ? 'Guardar cambios' : 'Guardar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
      <ErrorDialog
        open={sizeError !== null}
        message={sizeError ?? ''}
        title="Archivo demasiado grande"
        onClose={() => setSizeError(null)}
      />
    </Dialog>
  );
}

function Field({
  label,
  required,
  error,
  children,
  className,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={['space-y-1.5', className].filter(Boolean).join(' ')}>
      <Label>
        {label}
        {required && <span className="text-primary"> *</span>}
      </Label>
      {children}
      {error && <p className="text-xs font-medium text-destructive">{error}</p>}
    </div>
  );
}

/**
 * Precio con separadores de miles, como el resto de los importes de la app.
 * El form guarda el número; el texto queda local para no perder la coma
 * mientras se escribe ("8.500," todavía no es un número distinto de 8500).
 */
function PrecioInput({
  value,
  onChange,
  moneda,
  placeholder,
}: {
  value: number | undefined;
  onChange: (v: number | undefined) => void;
  moneda: 'ARS' | 'MXN' | 'USD';
  placeholder: string;
}) {
  const [texto, setTexto] = useState(() => (value == null ? '' : String(value).replace('.', ',')));
  // Si el form cambia el valor desde afuera (reset al abrir otro artículo), se toma ese.
  useEffect(() => {
    if ((parseMoney(texto) ?? undefined) !== value) setTexto(value == null ? '' : String(value).replace('.', ','));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <MoneyInput
      variant="filled"
      placeholder={placeholder}
      moneda={moneda}
      value={texto}
      onChange={(t) => {
        setTexto(t);
        onChange(parseMoney(t) ?? undefined);
      }}
    />
  );
}
